import { FC } from 'react';

import {
  S9ComponentConfigCategory,
  S9ComponentConfigRelease,
} from '@april9au/stack9-sdk';
import type { S9ComponentConfig } from '@april9au/stack9-sdk';

import { formatSlotDateTime } from '../../utils/datetime';

export const config: S9ComponentConfig = {
  name: 'SlotDateTimeCell',
  icon: '',
  category: S9ComponentConfigCategory.STACK9,
  version: S9ComponentConfigRelease.ALPHA,
  hasState: false,
  allowChildren: false,
  props: [],
};

/**
 * Props resolved from a column's `options.props` templates (see the Stack9 grid
 * `CustomComponent` renderer, which passes `resolveReferences(props, row)`).
 */
interface ResolvedProps {
  value?: string | null;
  timezone?: string | null;
}

interface SlotDateTimeCellProps {
  /** The full grid row (fallback source for value/timezone). */
  row?: {
    slot?: { start_dt?: string | null };
    event?: { timezone?: string | null };
  };
  /** Resolved `options.props` from the column configuration. */
  props?: ResolvedProps;
}

/**
 * Grid cell that renders an appointment slot instant in its event's timezone.
 *
 * Register under the name `SlotDateTimeCell` and reference it from a column with
 * `renderAs: "CustomComponent"`, passing the instant and the event timezone:
 *
 *   {
 *     "renderAs": "CustomComponent",
 *     "options": {
 *       "customComponentName": "SlotDateTimeCell",
 *       "props": { "value": "{{slot.start_dt}}", "timezone": "{{event.timezone}}" }
 *     }
 *   }
 *
 * The declarative `Date`/`Text` renderers can only format in the browser's local
 * zone — they have no per-row timezone option — which is why this exists.
 */
const SlotDateTimeCell: FC<SlotDateTimeCellProps> = ({ row, props }) => {
  const value = props?.value ?? row?.slot?.start_dt;
  const timezone = props?.timezone ?? row?.event?.timezone;

  return <>{formatSlotDateTime(value, timezone)}</>;
};

export default SlotDateTimeCell;
